import React from "react";
import { Pie } from "react-chartjs-2";
import { transformPieChartData } from "./chartUtils";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import "./index.scss";

const externalLabelsPlugin = {
    id: "externalLabels",
    afterDraw: (chart) => {
        const { ctx, chartArea } = chart;
        const meta = chart.getDatasetMeta(0);

        // Calculate center of the pie
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;

        // Calculate radius of the pie
        const radius = Math.min(
            (chartArea.right - chartArea.left) / 2,
            (chartArea.bottom - chartArea.top) / 2
        ) * 0.9; // Slightly reduce the pie size

        // Calculate label positions
        const dataTotal = chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
        interface LabelPosition {
            index: number;
            angle: number;
            value: number;
            percentage: number;
            label: string;
            baseX: number;
            baseY: number;
            finalX: number;
            finalY: number;
            textAlign: string;
        }
        
        const labelPositions: LabelPosition[] = [];
        
        // First pass: calculate initial positions
        meta.data.forEach((dataPoint, i) => {
            const angle = dataPoint.startAngle + (dataPoint.endAngle - dataPoint.startAngle) / 2;
            const value = chart.data.datasets[0].data[i];
            const percentage = Math.round((value / dataTotal) * 100);
            const label = chart.data.labels[i];
            
            // Calculate position based on angle and slice size
            // Use a minimum distance multiplier to ensure labels stay outside the pie
            const minDistanceMultiplier = 1.35;
            const labelDistanceMultiplier = Math.max(minDistanceMultiplier, 
                percentage < 3 ? 1.5 : minDistanceMultiplier); // Push small slices further out
            
            const baseX = centerX + Math.cos(angle) * radius * labelDistanceMultiplier;
            const baseY = centerY + Math.sin(angle) * radius * labelDistanceMultiplier;
            
            // Determine which side of the pie this label is on
            const rightSide = Math.cos(angle) > 0;
            const textAlign = rightSide ? "left" : "right";
            
            // Add horizontal offset based on which side of the pie
            const offsetX = rightSide ? 10 : -10;
            
            labelPositions.push({
                index: i,
                angle,
                value,
                percentage,
                label,
                baseX,
                baseY,
                finalX: baseX + offsetX,
                finalY: baseY,
                textAlign
            });
        });
        
        // Second pass: organize labels into left and right groups
        const leftLabels = labelPositions.filter(pos => pos.finalX < centerX);
        const rightLabels = labelPositions.filter(pos => pos.finalX >= centerX);
        
        // Sort by Y position
        leftLabels.sort((a, b) => a.finalY - b.finalY);
        rightLabels.sort((a, b) => a.finalY - b.finalY);
        
        // Ensure vertical spacing between labels
        const ensureVerticalSpacing = (labels, minSpacing) => {
            for (let i = 1; i < labels.length; i++) {
                const prev = labels[i - 1];
                const curr = labels[i];
                
                const gap = curr.finalY - prev.finalY;
                if (gap < minSpacing) {
                    // Push current label down
                    curr.finalY = prev.finalY + minSpacing;
                }
            }
        };
        
        ensureVerticalSpacing(leftLabels, 16);
        ensureVerticalSpacing(rightLabels, 16);
        
        // Combine sorted labels back into one array and sort by original index
        const sortedLabels = [...leftLabels, ...rightLabels].sort((a, b) => a.index - b.index);
        
        // Draw connecting lines and labels
        sortedLabels.forEach((position) => {
            // Get the label and value
            const { angle, label, percentage, finalX, finalY, textAlign } = position;
            
            // Calculate the point on the pie edge
            const edgeX = centerX + Math.cos(angle) * radius;
            const edgeY = centerY + Math.sin(angle) * radius;
            
            // Calculate intermediary points for the line
            const distanceFromCenter = Math.sqrt(
                Math.pow(finalX - centerX, 2) + Math.pow(finalY - centerY, 2)
            );
            
            const elbow1X = centerX + Math.cos(angle) * (radius + 10);
            const elbow1Y = centerY + Math.sin(angle) * (radius + 10);
            
            const elbow2X = textAlign === "left" ? elbow1X + 10 : elbow1X - 10;
            const elbow2Y = finalY;
            
            // Draw connecting line
            ctx.beginPath();
            ctx.moveTo(edgeX, edgeY);
            ctx.lineTo(elbow1X, elbow1Y);
            ctx.lineTo(elbow2X, elbow2Y);
            ctx.lineTo(finalX, finalY);
            ctx.strokeStyle = "rgba(0,0,0,0.6)";
            ctx.lineWidth = 0.5;
            ctx.stroke();
            
            // Draw label
            ctx.font = "10px Arial";
            ctx.fillStyle = "black";
            ctx.textAlign = textAlign;
            ctx.textBaseline = "middle";
            const labelText = `${label} ${percentage}%`;
            const textX = textAlign === "left" ? finalX + 5 : finalX - 4;
            ctx.fillText(labelText, textX, finalY);
        });
    },
};

interface ProductPieChartProps {
    chartsData: any[];
    title: string;
    tooltip: string;
}

const ProductPieChart: React.FC<ProductPieChartProps> = ({ chartsData, title, tooltip }) => {
    const { appColors }: any = useSelector((state: RootState) => state.common);
    const transformedData = transformPieChartData(chartsData, appColors?.appPrimaryColor) || { chartData: null };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        layout: {
            padding: {
                top: 80,
                bottom: 80,
                left: 80,
                right: 80,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.label || '';
                        const value = context.raw;
                        const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                        const percentage = Math.round((value / total) * 100);
                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            }
        },
    };

    return (
        <div style={{ height: 300 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
                <span>{title}</span>
                <Tooltip title={tooltip}>
                    <InfoCircleOutlined style={{ marginLeft: "8px" }} />
                </Tooltip>
            </div>

            {transformedData.chartData && transformedData.chartData.labels.length > 0 ? (
                <Pie
                    data={transformedData.chartData}
                    options={options}
                    plugins={[externalLabelsPlugin]}
                />
            ) : (
                <div className="no-data">No Data Available</div>
            )}
        </div>
    );
};

export default ProductPieChart;